import { useLayoutEffect } from 'react'

import { UseAnalyticsServiceHook } from '../analytics.types'

const noop = () => void 0

type GtagFunction = (...args: unknown[]) => void

type CommonAnalyticsParams = {
  // Stable app identifier (`AppConfig.id`), unaffected by renames. Group
  // reports by this rather than by `appName`, which changes with rebranding.
  appId: string
  appName: string
  appPath: string
  trackingSource?: string
  pageLocation?: string
  pageReferrer?: string
  userId?: number
  organizationId?: number
  originalOrganizationId?: number | null
}

type PageViewParams = CommonAnalyticsParams & {
  path: string
  title?: string
}

type TimeSpentParams = CommonAnalyticsParams & {
  timeSpentSeconds: number
  foregroundTimeSpent?: number
  measurementId?: string
  usageDay?: string
}

declare global {
  interface Window {
    gtag?: GtagFunction
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[]
  }
}

const getCommonEventParams = ({
  appId,
  appName,
  appPath,
  userId,
  organizationId,
  originalOrganizationId,
  trackingSource,
  pageLocation,
  pageReferrer
}: CommonAnalyticsParams) => ({
  app_id: appId,
  app_name: appName,
  app_path: appPath,
  ...(trackingSource ? { tracking_source: trackingSource } : {}),
  ...(pageLocation ? { page_location: pageLocation } : {}),
  ...(pageReferrer !== undefined ? { page_referrer: pageReferrer } : {}),
  ...(typeof userId === 'number' ? { user_ref: userId } : {}),
  ...(typeof organizationId === 'number' ? { org_id: organizationId } : {}),
  ...(typeof originalOrganizationId === 'number'
    ? { original_organization_id: originalOrganizationId }
    : {})
})

const setGoogleAnalyticsDisabled = (gtagId: string, disabled: boolean) => {
  if (typeof window === 'undefined' || !gtagId) return

  const windowWithGoogleAnalyticsFlag = window as unknown as Record<
    string,
    boolean | undefined
  >

  windowWithGoogleAnalyticsFlag[`ga-disable-${gtagId}`] = disabled

  window.gtag?.('consent', 'update', {
    analytics_storage: disabled ? 'denied' : 'granted'
  })
}

const ensureGoogleAnalytics = (gtagId: string): GtagFunction => {
  if (typeof window === 'undefined' || !gtagId) {
    return noop
  }

  if (window.gtag) {
    return window.gtag
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`
  document.head.prepend(script)

  window.dataLayer = window.dataLayer || []

  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }

  gtag('js', new Date())
  window.gtag = gtag

  return gtag
}

const configureGoogleAnalytics = (
  gtagId: string,
  userId?: number,
  organizationId?: number,
  originalOrganizationId?: number | null
): GtagFunction => {
  const gtag = ensureGoogleAnalytics(gtagId)

  setGoogleAnalyticsDisabled(gtagId, false)

  gtag('config', gtagId, {
    send_page_view: false,
    ...(typeof userId === 'number' ? { user_id: userId } : {})
  })

  gtag('set', {
    ...(typeof userId === 'number' ? { user_ref: userId } : {}),
    ...(typeof organizationId === 'number' ? { org_id: organizationId } : {}),
    ...(typeof originalOrganizationId === 'number'
      ? { original_organization_id: originalOrganizationId }
      : {})
  })

  return gtag
}

export const trackGoogleAnalyticsPageView = ({
  path,
  title,
  pageLocation,
  pageReferrer,
  appId,
  appName,
  appPath,
  userId,
  organizationId,
  originalOrganizationId,
  trackingSource
}: PageViewParams) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    ...getCommonEventParams({
      appId,
      appName,
      appPath,
      userId,
      organizationId,
      originalOrganizationId,
      trackingSource,
      pageLocation,
      pageReferrer
    })
  })
}

export const trackGoogleAnalyticsTimeSpent = ({
  appId,
  appName,
  appPath,
  timeSpentSeconds,
  foregroundTimeSpent,
  measurementId,
  usageDay,
  userId,
  organizationId,
  originalOrganizationId,
  trackingSource,
  pageLocation,
  pageReferrer
}: TimeSpentParams) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'time_spent_in_app', {
    ...getCommonEventParams({
      appId,
      appName,
      appPath,
      userId,
      organizationId,
      originalOrganizationId,
      trackingSource,
      pageLocation,
      pageReferrer
    }),
    time_spent_seconds: timeSpentSeconds,
    ...(typeof foregroundTimeSpent === 'number'
      ? { foreground_time_spent: foregroundTimeSpent }
      : {}),
    ...(measurementId ? { measurement_id: measurementId } : {}),
    ...(usageDay ? { usage_day: usageDay } : {}),
    event_category: 'engagement',
    event_label: appId,
    value: timeSpentSeconds,
    transport_type: 'beacon'
  })
}

export const useGoogleAnalytics: UseAnalyticsServiceHook<'googleAnalytics'> = (
  user,
  config = {
    enabled: true,
    id: ''
  }
) => {
  useLayoutEffect(() => {
    if (!config.id) return

    if (!config.enabled) {
      setGoogleAnalyticsDisabled(config.id, true)
      return
    }

    configureGoogleAnalytics(
      config.id,
      user?.id,
      user?.organization_id,
      user?.original_organization_id
    )
  }, [
    user?.id,
    user?.organization_id,
    user?.original_organization_id,
    config.enabled,
    config.id
  ])
}
